import { httpApi } from 'api/http.api';
import Contract, { ContractKind, ContractResponse } from 'types/contract';
import { ApiError, ResponseGeneral } from 'types';
import {
  ContractImportResult,
  parseContractImportResult,
} from 'types/contractImport';
import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';

const controller = `contract`;

type UploadContractsProps = {
  idFile: string;
  signal?: AbortSignal;
  startDate: Date;
  endDate?: Date;
  contractKind: string;
};

export const createOne = (
  contract: Contract
): Promise<AxiosResponse<Contract>> =>
  httpApi.post<Contract>(`${controller}`, { ...contract }).then((res) => res);

export const updateOne = (
  contract: Contract
): Promise<AxiosResponse<ContractResponse>> =>
  httpApi
    .put<ContractResponse>(`${controller}/${contract._id}`, { ...contract })
    .then((res) => res);

export const getAll = (): Promise<AxiosResponse<Contract[]>> =>
  httpApi.get<Contract[]>(`${controller}`, {}).then((res) => res);

export const getOne = (id: string): Promise<AxiosResponse<Contract>> =>
  httpApi.get<Contract>(`${controller}/${id}`, {}).then((res) => res);

export const getEmployeeContracts = (
  employeeId: string
): Promise<AxiosResponse<Contract[]>> =>
  httpApi
    .get<Contract[]>(`${controller}/getEmployeeContracts/${employeeId}`, {})
    .then((res) => res);

export const deleteOne = (
  id: string
): Promise<AxiosResponse<ResponseGeneral>> =>
  httpApi.delete<ResponseGeneral>(`${controller}/${id}`, {}).then((res) => res);

export const uploadContracts = async ({
  idFile,
  startDate,
  endDate,
  contractKind,
  signal,
}: UploadContractsProps): Promise<ContractImportResult> => {
  const response = await httpApi.post<unknown>(
    `${controller}/upload/${encodeURIComponent(idFile)}`,
    { startDate, endDate: endDate ?? undefined, contractKind },
    {
      signal,
      // Only this endpoint consumes validation rejections as typed results.
      // Other errors retain the shared auth redirects and safe Sentry handling.
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 422,
    }
  );
  const result = parseContractImportResult(response.status, response.data);
  if (result) return result;
  const error = new ApiError(
    502,
    'No se pudo confirmar el resultado de la importación.',
    {
      method: 'POST',
      url: '/contract',
      failureKind: 'http',
    }
  );
  Sentry.captureException(error);
  throw error;
};

export const getActiveWithoutSchedules = (): Promise<
  AxiosResponse<Contract[]>
> =>
  httpApi
    .get<Contract[]>(`${controller}/active-contracts-without-schedules`, {})
    .then((res) => res);

export const exportContractsUploadTemplate = (): Promise<
  AxiosResponse<string | Blob>
> =>
  httpApi
    .get<string | Blob>(`${controller}/export-upload-template`, {
      responseType: 'blob',
    })
    .then((res) => res);

// Contract kinds

export const getActiveContractKinds = (): Promise<
  AxiosResponse<ContractKind[]>
> =>
  httpApi
    .get<ContractKind[]>(`${controller}/getContractKinds?status=active`, {})
    .then((res) => res);
